import React from 'react';
import { Link } from 'react-router-dom';
import { FaDiscord } from 'react-icons/fa';
import { FiExternalLink, FiInfo } from 'react-icons/fi';
import generatedConfig from '../config.generated.json' assert { type: "json" };

const HYPERLIQUID_APP_URL = 'https://app.hyperliquid.xyz/trade';
const DISCORD_URL = 'https://discord.gg/asTcxUTzRv';
const SUPPORT_EMAIL = 'kitty@katbot.ai';

/**
 * SunsetNotice — shown wherever a door used to be.
 *
 * `variant="page"` replaces a gated route (dashboard, workspace).
 * `variant="panel"` sits inline above content that stays visible (/plans).
 *
 * NOTE: copy is intentionally dateless until the wind-down window is set
 * (KAT-230 Q1). Add the cut date here when it is decided.
 */
export const SunsetNotice = ({
  variant = 'page',
  config,
  title = 'Katbot is no longer accepting new sessions',
  children,
}) => {
  // config is threaded in where a page already has it; fall back to the injected
  // env config so gates without a config prop (e.g. ProtectedRoute) still work.
  const docsUrl = config?.docsUrl || generatedConfig?.docsUrl || 'https://docs.katbot.ai';

  if (variant === 'panel') {
    return (
      <div className="w-full rounded-xl border border-amber-500/30 bg-amber-900/20 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <FiInfo className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
          <div className="flex flex-col gap-2">
            <h2 className="m-0 text-base font-semibold text-amber-200">{title}</h2>
            <div className="text-sm leading-relaxed text-amber-100/80">{children}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-start gap-5 px-5 py-16 text-white sm:py-24">
      <p className="m-0 font-mono text-xs uppercase tracking-widest text-amber-400">
        Service notice
      </p>

      <h1 className="m-0 bg-gradient-to-r from-green-300 via-blue-500 to-purple-600 bg-clip-text text-3xl font-extrabold leading-tight text-transparent sm:text-4xl">
        {title}
      </h1>

      <p className="m-0 text-lg text-gray-300">
        Katbot has closed access to the trading workspace. Recommendations, research runs,
        and trade execution are no longer available through this site.
      </p>

      <div className="w-full rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4">
        <h2 className="m-0 text-sm font-semibold text-cyan-300">Your funds and positions are unaffected</h2>
        <p className="mt-2 mb-0 text-sm leading-relaxed text-gray-300">
          Katbot is non-custodial and never held your funds — your wallet and agent keys are
          yours. Any open Hyperliquid position can be viewed and closed directly on Hyperliquid.
        </p>
        <a
          href={HYPERLIQUID_APP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 px-4 py-2 text-sm font-semibold text-white no-underline transition-all duration-200 hover:from-cyan-400 hover:to-blue-500"
        >
          Open Hyperliquid <FiExternalLink />
        </a>
      </div>

      {children}

      <div className="flex flex-wrap items-center gap-3">
        <a
          href={docsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-5 py-2.5 font-semibold text-white no-underline transition-all duration-200 hover:border-white/40 hover:bg-white/5"
        >
          Read the Docs
        </a>
        <a
          href={DISCORD_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-5 py-2.5 font-semibold text-white no-underline transition-all duration-200 hover:border-white/40 hover:bg-white/5"
        >
          <FaDiscord className="text-lg" />
          Join Discord
        </a>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-1 py-2.5 font-semibold text-gray-400 underline transition-colors duration-200 hover:text-white"
        >
          Back to home
        </Link>
      </div>

      <p className="m-0 text-sm text-gray-500">
        Questions? Reach us at{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-cyan-400 hover:text-cyan-300">
          {SUPPORT_EMAIL}
        </a>
        .
      </p>
    </div>
  );
};

export default SunsetNotice;
